import type { CatalogCharacter, CatalogMedia, CosplayCandidate, CosplayPreferences, CosplayProject, WardrobeItem } from "./types";

const narratorPattern = /(^|\b)(narrator|narration)(\b|$)|ナレーター|語り手|해설자|내레이터/i;

export function isEligibleCosplayCharacter(character: CatalogCharacter): boolean {
  return (character.role === "MAIN" || character.role === "SUPPORTING")
    && Boolean(character.image)
    && /^https:\/\/anilist\.co\/character\/\d+\/?$/.test(character.siteUrl)
    && !narratorPattern.test(character.name)
    && !narratorPattern.test(character.nativeName ?? "");
}

export interface RecommendationHistory {
  preferredGenres: string[];
  completedCharacters: string[];
  reusableItemCount: number;
}

export function buildRecommendationHistory(projects: CosplayProject[], wardrobe: WardrobeItem[]): RecommendationHistory {
  const completed = projects.filter((project) => project.tasks.every((task) => task.status === "done"));
  const genreCounts = new Map<string, number>();
  for (const project of completed) {
    for (const genre of project.preferences.genres) genreCounts.set(genre, (genreCounts.get(genre) ?? 0) + 1);
  }
  return {
    preferredGenres: [...genreCounts].sort((left, right) => right[1] - left[1]).map(([genre]) => genre).slice(0, 5),
    completedCharacters: completed.map((project) => project.characterName),
    reusableItemCount: wardrobe.filter((item) => item.ready && ["owned", "borrow"].includes(item.status)).length,
  };
}

export function recommendCharacters(mediaList: CatalogMedia[], preferences: CosplayPreferences, history?: RecommendationHistory): CosplayCandidate[] {
  const candidates: Array<CosplayCandidate & { sortRank: number }> = [];
  for (const media of mediaList.filter((item) => item.type === preferences.mediaType)) {
    const genreMatches = media.genres.filter((genre) => preferences.genres.includes(genre));
    const historyGenres = media.genres.filter((genre) => history?.preferredGenres.includes(genre));
    const characters = media.characters.filter(isEligibleCosplayCharacter).slice(0, 3);
    for (const character of characters) {
      const alreadyCompleted = history?.completedCharacters.includes(character.name) ?? false;
      const reasons = genreMatches.length > 0
        ? [`고른 ${genreMatches.join("·")} 장르와 작품 분류가 겹쳐요.`]
        : ["고른 장르와 직접 겹치지 않는 탐색 후보예요."];
      if (historyGenres.length > 0) reasons.push(`완주 기록의 ${historyGenres.join("·")} 취향과 이어져요.`);
      if (media.seasonYear) reasons.push(`${media.seasonYear}년 작품 · AniList의 작품·캐릭터 정보로 확인했어요.`);
      if ((history?.reusableItemCount ?? 0) > 0) reasons.push(`보유 파츠 ${history?.reusableItemCount}개는 프로젝트를 만든 뒤 실제 재사용 여부를 확인할 수 있어요.`);
      const tradeoffs = [
        "이 캐릭터의 의상 버전·실루엣·노출·활동성은 이 자료만으로 알 수 없어요.",
        "실제 예산·실측·제작 난도는 선택한 버전과 판매처·제작 견적으로 확인해야 해요.",
        "행사 규정과 촬영 가능 범위는 프로젝트에서 공식 출처를 따로 확인해요.",
      ];
      if (alreadyCompleted) tradeoffs.push("이미 완주한 캐릭터예요. 새 버전이 아니라면 기존 기록을 먼저 열어보세요.");
      candidates.push({
        id: `${media.id}:${character.id}`, media, character, tasteMatchCount: genreMatches.length,
        reasons, tradeoffs: tradeoffs.slice(0, 4),
        sortRank: genreMatches.length * 100 + historyGenres.length * 20 - (alreadyCompleted ? 80 : 0),
      });
    }
  }
  return candidates
    .sort((left, right) => right.sortRank - left.sortRank || (right.media.popularity ?? 0) - (left.media.popularity ?? 0) || left.character.id.localeCompare(right.character.id))
    .slice(0, 18)
    .map(({ sortRank: _sortRank, ...candidate }) => candidate);
}
